import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { EventService } from '../../service/event.service';
import { Component, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef } from '@angular/core';
import { register } from 'swiper/element/bundle';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { SwalService } from '../../service/swal.service';
import { TourService } from '../../service/tour.service';
import { foldPackagesIntoServices } from '../../utility/packages-migration';
import { discountFields, effectivePrice } from '../../utility/service-price';
import { ClearZeroOnFocusDirective } from '../../utility/clear-zero-on-focus.directive';
import { PlacesAutocompleteDirective, SelectedPlace } from '../../utility/places-autocomplete.directive';
import { GoogleMapsLoaderService } from '../../service/google-maps-loader.service';
import { SubscriptionService } from '../../service/subscription.service';
import { EventDetailStore } from './event-detail.store';
import { PagamentiTabComponent } from './tabs/pagamenti-tab.component';
import { DocumentiTabComponent } from './tabs/documenti-tab.component';
import { ContrattoTabComponent } from './tabs/contratto-tab.component';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';
import { TimePickerComponent } from '../../components/time-picker/time-picker.component';
import { PhoneInputComponent } from '../../utility/phone-input/phone-input.component';
import { shareOrCopy, daysFromToday } from '../../utility/share';
import { logoUrl } from '../../utility/logo';
import { PERIOD_OPTIONS, cleanDateHint, periodLabel, periodYears } from '../../utility/event-period';
import { buildQuoteEmail, copyRichText, MAIL_LOCALES, MailLang, translatorFor } from '../../utility/quote-email';
import { discountLabel } from '../../utility/totals';
import { hasPriceDisclaimer, isQuote, needsDate } from '../../utility/quote';
import {
  QuoteVariant,
  activeVariantId,
  addVariant,
  removeVariant,
  switchVariant,
  syncActiveVariant,
  variantList,
  variantTotal,
} from '../../utility/quote-variants';
import { AWAY_RELOAD_MS, onReturnAfter } from '../../utility/foreground';
register();

type Tone = 'ok' | 'warn' | 'danger' | 'neutral';
interface StatusChip { key: string; tone: Tone; params?: Record<string, any>; }

/** Campo che serve al contratto, con la scheda e la sotto-sezione dove si compila. */
interface MissingField {
  key: 'name' | 'lastname' | 'date' | 'location' | 'address' | 'dob' | 'pob' | 'services';
  tab: number;
  sub: string;
}

@Component({
  selector: 'app-event-detail',
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgxDropzoneModule,
    TranslatePipe,
    ClearZeroOnFocusDirective,
    PlacesAutocompleteDirective,
    PagamentiTabComponent,
    DocumentiTabComponent,
    ContrattoTabComponent,
    IconComponent,
    TimePickerComponent,
    LoadErrorComponent,
    PhoneInputComponent,
  ],
  providers: [EventDetailStore],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EventDetailComponent implements OnInit, OnDestroy {
  Math = Math;
  eventVisibilityOpen = false; // pannello visibilità collaboratori/team (a scomparsa)
  // Accorpate: Evento+Prova, Cliente+Servizi+Documenti+Contratto (meno schede, più fluido da mobile)
  readonly tourTabIds = ['tour-tab-evento', 'tour-tab-servizi', 'tour-tab-contratto', 'tour-tab-pagamenti', 'tour-tab-collaboratori'];

  tabs: string[] = [
    'riepilogo.tabs.wedding',
    'riepilogo.tabs.logistics',
    'riepilogo.tabs.contract',
    'riepilogo.tabs.payments',
    'riepilogo.tabs.collaborators',
  ];
  readonly tabIcons = ['calendar-heart', 'sparkle', 'file', 'card', 'users'];

  moreMenuOpen = false;
  mailOpen = false;
  mailIncludeLink = true;
  mailLang: MailLang = 'it';
  mailDays = 30;
  mailValidityMode: 'days' | 'date' = 'days';
  mailUntil = '';
  private profileQuoteDays = 30;
  // Link creato da questa finestra: non ancora condiviso, si può rigenerare senza chiedere
  private mailLinkFresh = false;
  // Listino del profilo non arrivato: si mostra l'errore, non un falso "nessun servizio"
  profileLoadError = false;
  private stopReturnWatch?: () => void;
  readonly mailLangs: MailLang[] = ['it', 'en', 'es'];
  mailHtml: SafeHtml = '';
  private mailPlain = '';
  private mailRaw = '';
  profileSignature = { name: '', phone: '', email: '' };
  private profileLogo: string | null = null;
  isMobile = false;
  private mobileQuery?: MediaQueryList;
  private onMobileChange = (e: MediaQueryListEvent) => (this.isMobile = e.matches);
  activeTab: number = 0;

  // Sotto-sezione aperta dentro la scheda corrente (accordion mobile; su desktop
  // restano tutte visibili via CSS, vedi .subsection-body in event-detail.component.css)
  activeSub: string | null = 'evento';

  toggleSub(key: string): void {
    this.activeSub = this.activeSub === key ? null : key;
  }
  ownerInfo: any = null;

  selectedFiles: File[] = [];
  isUploadingImages = false;

  lightboxOpen = false;
  lightboxIndex = 0;
  @ViewChild('lightboxSwiper') lightboxSwiperRef?: ElementRef;

  profileTeam: any[] = [];
  teamAccessMembers: any[] = [];
  profileServices: any[] = [];

  currentUserId: string | null = null;
  professionalCategories: string[] = [];

  // ── STATO CONDIVISO (delega allo store, unica fonte di verità) ──────────────
  get event(): any { return this.store.event; }
  set event(v: any) { this.store.event = v; }
  get eventId(): string | null { return this.store.eventId; }
  set eventId(v: string | null) { this.store.eventId = v; }
  get editMode(): boolean { return this.store.editMode; }
  set editMode(v: boolean) { this.store.editMode = v; }
  get isCollaborator(): boolean { return this.store.isCollaborator; }
  set isCollaborator(v: boolean) { this.store.isCollaborator = v; }

  canSee(section: string): boolean { return this.store.canSee(section); }

  get clientFullName(): string { return this.store.clientFullName; }
  get selectedServices(): any[] { return this.store.selectedServices; }
  get totalServices(): number { return this.store.totalServices; }
  get testCost(): number { return this.store.testCost; }
  get travelFee(): number { return this.store.travelFee; }
  get totalAll(): number { return this.store.totalAll; }
  get discountEuro(): number { return this.store.discountEuro; }
  get discount(): any { return this.event.financial.discount; }
  get discountLabel(): string {
    return discountLabel(this.event, this.translate.instant('riepilogo.discount.label'));
  }

  setDiscountType(type: 'fixed' | 'percent'): void {
    this.discount.type = type;
  }
  get depositCost(): number { return this.store.depositCost; }
  get depositPaid(): boolean { return this.store.depositPaid; }
  get balancePaid(): boolean { return this.store.balancePaid; }
  get saldo(): number { return this.store.saldo; }
  get incassato(): number { return this.store.incassato; }
  get daIncassare(): number { return this.store.daIncassare; }

  canSeeTab(i: number): boolean {
    if (!this.isCollaborator) return true;
    switch (i) {
      // Evento = Evento + Prova/Sopralluogo accorpati: visibile se una delle due lo è
      case 0: return this.canSee('event_data') || this.canSee('client') || this.canSee('trial') || this.canSee('photos') || this.canSee('participants');
      case 1: return this.canSee('services');
      case 2: return this.canSee('documents'); // il contratto resta del titolare, al collaboratore solo i documenti
      case 3: return false; // pagamenti mai visibili al collaboratore
      case 4: return false; // tab Collaboratori mai visibile al collaboratore
      default: return true;
    }
  }

  // ── Override visibilità per questo evento ──
  readonly visibilitySections: { key: string; label: string }[] = [
    { key: 'event_data', label: 'profile.visibility.sections.event_data' },
    { key: 'trial', label: 'profile.visibility.sections.trial' },
    { key: 'photos', label: 'profile.visibility.sections.photos' },
    { key: 'client', label: 'profile.visibility.sections.client' },
    { key: 'services', label: 'profile.visibility.sections.services' },
    { key: 'participants', label: 'profile.visibility.sections.participants' },
    { key: 'contract', label: 'profile.visibility.sections.contract' },
    { key: 'documents', label: 'profile.visibility.sections.documents' },
  ];
  eventCollaboratorVisibility: Record<string, boolean> = {};
  eventTeamVisibility: Record<string, boolean> = {};

  private initEventVisibility() {
    const v = this.event?._visibility ?? {};
    this.eventCollaboratorVisibility = { ...(v.collaborator ?? {}) };
    this.eventTeamVisibility = { ...(v.team ?? {}) };
  }

  toggleEventCollaboratorVisibility(key: string) {
    this.eventCollaboratorVisibility[key] = !this.eventCollaboratorVisibility[key];
  }

  toggleEventTeamVisibility(key: string) {
    this.eventTeamVisibility[key] = !this.eventTeamVisibility[key];
  }

  saveEventVisibility() {
    this.ms.updateVisibilityOverride(
      this.eventId!,
      {
        collaborator_visibility_override: this.eventCollaboratorVisibility,
        team_visibility_override: this.eventTeamVisibility,
      },
      () => {
        if (this.event) {
          this.event._visibility = {
            collaborator: { ...this.eventCollaboratorVisibility },
            team: { ...this.eventTeamVisibility },
          };
        }
        this.swal.success('Visibilità evento salvata');
      },
      () => this.swal.errorDialog('Errore', 'Salvataggio visibilità fallito'),
    );
  }

  resetEventVisibility() {
    this.ms.updateVisibilityOverride(
      this.eventId!,
      { collaborator_visibility_override: null, team_visibility_override: null },
      () => {
        this.ms.getMatrimonio(
          this.eventId!,
          (data: any) => {
            this.event = data;
            if (!this.event.financial) this.event.financial = {};
            if (!this.event.financial.travel_fee)
              this.event.financial.travel_fee = { cost: 0 };
            this.initEventVisibility();
            this.swal.success('Ripristinati i default del profilo');
          },
          () => {},
        );
      },
      () => this.swal.errorDialog('Errore', 'Ripristino fallito'),
    );
  }

  get isBeautyCategory(): boolean {
    if (!this.professionalCategories.length) return true;
    return this.professionalCategories.some(c => ['makeup', 'hair'].includes(c));
  }

  // ── LIGHTBOX ──────────────────────────────────────────────────────────────

  openInMaps(address?: string) {
    if (!address || !address.trim()) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
    window.open(url, '_blank', 'noopener');
  }

  get mapsEnabled(): boolean {
    return !!environment.googleMapsApiKey;
  }

  onLocationPlace(place: SelectedPlace) {
    if (this.event?.event) this.event.event.location_place = place;
  }

  onTestLocationPlace(place: SelectedPlace) {
    if (this.event?.event) this.event.event.test_location_place = place;
  }

  baseAddress = '';
  travelRatePerKm = 0;
  calculatingTravel = false;
  travelBreakdown = '';

  private mapUrlCache = new Map<string, SafeResourceUrl>();

  mapUrl(address?: string): SafeResourceUrl | null {
    const a = (address || '').trim();
    if (!a) return null;
    let url = this.mapUrlCache.get(a);
    if (!url) {
      const src = `https://www.google.com/maps/embed/v1/place?key=${environment.googleMapsApiKey}&q=${encodeURIComponent(a)}`;
      url = this.sanitizer.bypassSecurityTrustResourceUrl(src);
      this.mapUrlCache.set(a, url);
    }
    return url;
  }

  async calculateTravelFee() {
    if (!this.subscription.requirePro('Il calcolo automatico della trasferta è disponibile con il piano Pro o Agency.')) {
      return;
    }
    if (!this.baseAddress?.trim()) {
      this.swal.errorDialog(
        this.translate.instant('swal.error'),
        this.translate.instant('riepilogo.travel.no_base'),
      );
      return;
    }
    const destination = this.travelDestination();
    if (!destination) {
      this.swal.errorDialog(
        this.translate.instant('swal.error'),
        this.translate.instant('riepilogo.travel.no_location'),
      );
      return;
    }

    this.calculatingTravel = true;
    try {
      await this.mapsLoader.load();
      const service = new google.maps.DistanceMatrixService();
      const res = await service.getDistanceMatrix({
        origins: [this.baseAddress],
        destinations: [destination],
        travelMode: google.maps.TravelMode.DRIVING,
      });
      const element = res.rows?.[0]?.elements?.[0];
      if (!element || element.status !== 'OK') {
        throw new Error('no-route');
      }
      const km = element.distance.value / 1000;
      const roundTripKm = km * 2;
      const cost = Math.round(roundTripKm * this.travelRatePerKm * 100) / 100;

      if (!this.event.financial) this.event.financial = {};
      if (!this.event.financial.travel_fee) this.event.financial.travel_fee = {};

      if (this.travelRatePerKm <= 0) {
        this.travelBreakdown = this.translate.instant('riepilogo.travel.no_rate', {
          km: roundTripKm.toFixed(1),
        });
        this.swal.errorDialog(
          this.translate.instant('swal.error'),
          this.translate.instant('riepilogo.travel.no_rate', {
            km: roundTripKm.toFixed(1),
          }),
        );
        return;
      }

      this.event.financial.travel_fee.cost = cost;
      this.event.financial.travel_fee.distance_km =
        Math.round(roundTripKm * 10) / 10;
      this.travelBreakdown = this.translate.instant('riepilogo.travel.breakdown', {
        km: roundTripKm.toFixed(1),
        rate: this.travelRatePerKm,
      });
    } catch {
      this.swal.errorDialog(
        this.translate.instant('swal.error'),
        this.translate.instant('riepilogo.travel.error'),
      );
    } finally {
      this.calculatingTravel = false;
    }
  }

  private travelDestination(): string | google.maps.LatLngLiteral | null {
    const place = this.event?.event?.location_place;
    if (place && place.lat != null && place.lng != null) {
      return { lat: place.lat, lng: place.lng };
    }
    const loc = this.event?.event?.location?.trim();
    return loc || null;
  }

  openLightbox(index: number) {
    this.lightboxIndex = index;
    this.lightboxOpen = true;
    document.body.style.overflow = 'hidden';
    // initial-slide non è affidabile con *ngIf: forziamo la slide dopo l'init dello swiper
    setTimeout(() => {
      const swiper = this.lightboxSwiperRef?.nativeElement?.swiper;
      if (swiper) swiper.slideTo(index, 0);
    });
  }

  closeLightbox() {
    this.lightboxOpen = false;
    document.body.style.overflow = '';
  }

  onSlideChange(e: any) {
    this.lightboxIndex = e.detail[0].activeIndex;
  }

  goToSlide(i: number) {
    this.lightboxIndex = i;
    const swiper = this.lightboxSwiperRef?.nativeElement?.swiper;
    if (swiper) swiper.slideTo(i);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ms: EventService,
    private swal: SwalService,
    private translate: TranslateService,
    private tour: TourService,
    private sanitizer: DomSanitizer,
    private mapsLoader: GoogleMapsLoaderService,
    public store: EventDetailStore,
    private subscription: SubscriptionService,
  ) {}

  get canProMaps(): boolean {
    return this.subscription.canPro();
  }

  get canPro(): boolean {
    return this.subscription.canPro();
  }

  goToPlans() {
    this.router.navigate(['/abbonamenti']);
  }

  private get locale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'it';
    const map: Record<string, string> = { it: 'it-IT', en: 'en-GB', es: 'es-ES' };
    return map[lang] ?? 'it-IT';
  }

  ngOnInit() {
    this.mobileQuery = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mobileQuery.matches;
    this.mobileQuery.addEventListener('change', this.onMobileChange);
    this.tour.evento();
    this.subscription.loadEntitlements();
    this.eventId = this.route.snapshot.paramMap.get('id')!;

    this.loadEvent(true);

    this.ms.getTeamAccessMembers((members: any[]) => {
      this.teamAccessMembers = (members ?? []).map((m: any) => ({
        name: m.user?.name || m.user?.email || '',
        lastname: m.user?.lastname || '',
        email: m.user?.email,
        service: {},
        available: true,
        _hasAccess: true,
      }));
    });

    this.loadProfile();
    this.stopReturnWatch = onReturnAfter(AWAY_RELOAD_MS, () => this.refreshOnReturn());
  }

  // PWA ripresa dopo un po': rilegge listino ed evento. In modifica o con la
  // finestra mail aperta l'evento resta com'è, per non perdere quello che si sta scrivendo.
  private refreshOnReturn() {
    this.loadProfile();
    if (!this.editMode && !this.mailOpen) this.loadEvent(false);
  }

  private loadEvent(initial: boolean) {
    this.ms.getMatrimonio(
      this.eventId!,
      (data) => {
        this.event = data;
        this.initEventVisibility();

        if (this.event.event.show_total === undefined)
          this.event.event.show_total = false;
        if (!this.event.financial) this.event.financial = {};
        if (!this.event.financial.travel_fee)
          this.event.financial.travel_fee = { cost: 0 };
        if (!this.event.schedule)
          this.event.schedule = {
            hair: 0,
            makeup: 0,
            team: { people: [] },
          };
        if (!this.event.schedule.team)
          this.event.schedule.team = { people: [] };
        if (!this.event.schedule.team.people)
          this.event.schedule.team.people = [];
        if (!this.event.schedule.partecipanti)
          this.event.schedule.partecipanti = [];
        if (!this.event.event.services)
          this.event.event.services = [];
        if (this.event.event.show_prices === undefined)
          this.event.event.show_prices = true;

        // Retro-compat: fonde eventuali pacchetti legacy nei servizi
        foldPackagesIntoServices(this.event);
        this.periodYears = periodYears(6, this.event.event?.date_hint?.year);

        // Proposta salvata: in lettura cambiare proposta serve solo a guardarla
        this.savedVariantId = activeVariantId(this.event);

        if (initial) {
          if (this.route.snapshot.queryParamMap.get('contratto') === 'anteprima') {
            this.store.autoPreview = true;
            this.setTab(2);
            this.router.navigate([], { queryParams: {}, replaceUrl: true });
          }
        }

        if (this.currentUserId) {
          this.checkCollaboratorRole();
          if (this.isCollaborator) {
            this.ms.getOwnerInfo(
              this.eventId!,
              (d) => (this.ownerInfo = d),
            );
          }
        }
      },
      () => {
        if (initial) this.swal.error('Errore nel caricamento');
      },
    );
  }

  loadProfile() {
    this.ms.getProfile((profile: any) => {
      this.profileLoadError = false;
      this.profileTeam = profile.team ?? [];
      this.profileServices = profile.services ?? [];
      this.currentUserId = profile._id;
      this.professionalCategories = profile.user?.professional_categories ?? [];
      const contract = profile?.contract ?? profile?.user?.contract ?? {};
      this.profileQuoteDays = +profile.quote_validity_days || 30;
      this.profileLogo = contract.logo ? logoUrl(profile._id) : null;
      this.profileSignature = {
        name: contract.business_name || contract.full_name || '',
        phone: contract.telephone || '',
        email: contract.email || profile.user?.email || '',
      };
      this.baseAddress = contract.address ?? '';
      this.travelRatePerKm = +contract.travel_rate_per_km || 0;

      if (this.event) {
        this.checkCollaboratorRole();
        if (this.isCollaborator) {
          this.ms.getOwnerInfo(this.eventId!, (d) => (this.ownerInfo = d));
        }
      }
    }, () => {
      // Una rilettura fallita lascia il listino già caricato
      this.profileLoadError = !this.profileServices.length;
    });
  }

  checkCollaboratorRole() {
    if (!this.event || !this.currentUserId) return;
    const isOwner = this.event.user_id === this.currentUserId;
    if (isOwner) {
      this.isCollaborator = false;
      return;
    }
    const collaborators = this.event.collaborators ?? [];
    this.isCollaborator = collaborators.some(
      (c: any) => c.user_id === this.currentUserId && c.status === 'accepted',
    );
    // I collaboratori non modificano: forza la sola lettura
    if (this.isCollaborator) this.editMode = false;
  }

  // ── TAB ───────────────────────────────────────────────────────────────────

  setTab(index: number): void {
    this.activeTab = index;
    this.moreMenuOpen = false;
    // Riapre la prima sotto-sezione della scheda appena selezionata (accordion mobile)
    const firstSub: Record<number, string | null> = {
      0: 'evento',
      1: 'servizi',
      2: this.isCollaborator ? 'documenti' : 'contratto',
    };
    this.activeSub = firstSub[index] ?? null;
  }

  openContractSection(): void {
    this.setTab(2);
    this.activeSub = 'contratto';
  }

  // ── COSA MANCA PER IL CONTRATTO ───────────────────────────────────────────
  // La scheda si apre sempre in lettura: qui le voci da completare prima del
  // contratto (stessa regola di Crea evento), ognuna apre la modifica nella scheda giusta.

  readonly missingFields: MissingField[] = [
    { key: 'name', tab: 0, sub: 'cliente' },
    { key: 'lastname', tab: 0, sub: 'cliente' },
    { key: 'date', tab: 0, sub: 'evento' },
    { key: 'location', tab: 0, sub: 'evento' },
    { key: 'address', tab: 0, sub: 'cliente' },
    { key: 'dob', tab: 0, sub: 'cliente' },
    { key: 'pob', tab: 0, sub: 'cliente' },
    { key: 'services', tab: 1, sub: 'servizi' },
  ];

  get missingForContract(): MissingField[] {
    const ev = this.event;
    if (!ev || this.isCollaborator || this.editMode || this.isQuote || ev.event?.contract?.file) return [];
    const c = ev.client ?? {};
    const missing: Record<MissingField['key'], boolean> = {
      name: !c.name,
      lastname: !c.lastname,
      date: !ev.event?.date,
      location: !ev.event?.location,
      address: !c.address,
      dob: !c.dateOfBirth,
      pob: !c.placeOfBirth,
      services: !(ev.event?.services ?? []).some((s: any) => s.selected),
    };
    return this.missingFields.filter((f) => missing[f.key]);
  }

  /** Apre la modifica nella scheda e nella sotto-sezione del campo da completare. */
  editAt(tab: number, sub: string): void {
    this.setTab(tab);
    this.activeSub = sub;
    if (!this.editMode) this.startEdit();
  }

  // ── EDIT MODE ─────────────────────────────────────────────────────────────

  startEdit() {
    this.store.takeSnapshot();
    this.editMode = true;
  }

  saveEdit(then?: () => void) {
    const ev = this.event.event;
    if (ev) ev.date_hint = cleanDateHint(ev.date, ev.date_hint);
    if (ev?.services) ev.services = ev.services.filter((s: any) => !s.custom || s.name?.trim());
    syncActiveVariant(this.event);
    this.ms.saveMatrimonio(this.eventId!, this.event, (saved: any) => {
      this.editMode = false;
      this.store.snapshot = null;
      this.savedVariantId = activeVariantId(this.event);
      if (saved?.quote_link_expired) {
        this.event.quote_request = saved.quote_request;
        // Se sta per reinviarlo subito, l'avviso è superfluo
        if (!then) {
          this.swal.dialog(
            'info',
            this.translate.instant('quote.link_expired_title'),
            this.translate.instant('quote.link_expired'),
          );
        }
      }
      if (then) then();
      if (saved?.signature_link_expired) {
        this.event.signature_request = saved.signature_request;
        this.swal.dialog(
          'info',
          this.translate.instant('riepilogo.signature_link_expired_title'),
          this.translate.instant('riepilogo.signature_link_expired'),
        );
      }
    });
  }

  // Ripristina solo i dati modificabili col form: foto, documenti, contratto,
  // collaboratori e stati "incassato" vengono salvati subito e restano come sono.
  cancelEdit() {
    const snap = this.store.snapshot;
    if (snap && this.event) {
      const restored = JSON.parse(JSON.stringify(snap));
      const contract = this.event.event?.contract;
      const paidState = (part: any) => ({ paid: part?.paid, paid_at: part?.paid_at });
      const deposit = paidState(this.event.financial?.deposit);
      const balance = paidState(this.event.financial?.balance);

      this.event.client = restored.client;
      this.event.event = restored.event;
      if (contract) this.event.event.contract = contract;
      this.event.schedule = restored.schedule;
      this.event.financial = restored.financial ?? {};
      this.event.financial.deposit = { ...(this.event.financial.deposit ?? {}), ...deposit };
      this.event.financial.balance = { ...(this.event.financial.balance ?? {}), ...balance };
    }
    this.store.snapshot = null;
    this.editMode = false;
    this.showTeamPicker = false;
  }

  // ── PREVENTIVO ────────────────────────────────────────────────────────────

  get isQuote(): boolean {
    return isQuote(this.event);
  }

  get priceDisclaimer(): boolean {
    return hasPriceDisclaimer(this.event);
  }

  // Proposte alternative dello stesso preventivo (utility/quote-variants.ts)
  get quoteVariants(): QuoteVariant[] {
    return variantList(this.event);
  }

  get activeVariant(): string | null {
    return activeVariantId(this.event);
  }

  get activeQuoteVariant(): QuoteVariant | null {
    return this.quoteVariants.find((v) => v.id === this.activeVariant) ?? null;
  }

  quoteVariantTotal(v: QuoteVariant): number {
    return variantTotal(this.event, v);
  }

  /** "€ 600–780" per il riepilogo nel riquadro del preventivo. */
  get quoteVariantRange(): string {
    const totals = this.quoteVariants.map((v) => this.quoteVariantTotal(v));
    const fmt = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
    const min = Math.min(...totals);
    const max = Math.max(...totals);
    return min === max ? `€ ${fmt(min)}` : `€ ${fmt(min)}–${fmt(max)}`;
  }

  /**
   * In lettura cambiare proposta serve solo a guardarla e non si salva; in modifica
   * la proposta scelta si salva con il resto della scheda.
   */
  selectQuoteVariant(id: string): void {
    switchVariant(this.event, id);
  }

  /** Proposta attiva come salvata sul server (vedi selectQuoteVariant). */
  private savedVariantId: string | null = null;

  addQuoteVariant(): void {
    if (!this.editMode) this.startEdit();
    const n = Math.max(this.quoteVariants.length, 1) + 1;
    addVariant(
      this.event,
      this.translate.instant('quote.variants.default_name', { n: 1 }),
      this.translate.instant('quote.variants.default_name', { n }),
    );
    this.setTab(1);
  }

  async removeQuoteVariant(v: QuoteVariant): Promise<void> {
    const res = await this.swal.confirm(
      this.translate.instant('quote.variants.remove_title'),
      this.translate.instant('quote.variants.remove_text', { name: v.name }),
      this.translate.instant('quote.variants.remove'),
      this.translate.instant('common.cancel'),
    );
    if (res.isConfirmed) removeVariant(this.event, v.id);
  }

  get quoteLink(): string | null {
    const qr = this.event?.quote_request;
    if (!qr?.token) return null;
    if (qr.expires_at && new Date(qr.expires_at) <= new Date()) return null;
    return `${window.location.origin}/p/${qr.token}`;
  }

  get quoteStage(): 'draft' | 'sent' | 'expired' {
    if (this.quoteLink) return 'sent';
    return this.event?.quote_request?.token ? 'expired' : 'draft';
  }

  get quoteExpiry(): string {
    const d = this.event?.quote_request?.expires_at;
    return d ? new Date(d).toLocaleDateString(this.locale) : '';
  }

  // In modifica salva prima: il link deve mostrare i dati che si vedono a schermo
  sendQuote() {
    if (this.editMode) this.saveEdit(() => this.createQuoteLink());
    else this.createQuoteLink();
  }

  private createQuoteLink(onDone?: () => void, validity?: { days?: number; expires_at?: string }) {
    this.ms.generateQuoteLink(
      this.eventId!,
      (data) => {
        this.event.quote_request = {
          ...(this.event.quote_request ?? {}),
          token: data.link,
          expires_at: data.expires_at,
          accepted_at: null,
        };
        if (onDone) onDone();
        else this.shareQuote();
      },
      (err: any) => {
        if (err?.status === 403) return;
        this.swal.errorDialog(
          this.translate.instant('swal.error'),
          err?.error?.message || this.translate.instant('quote.link_error'),
        );
      },
      validity,
    );
  }

  // ── MAIL PREVENTIVO ───────────────────────────────────────────────────────

  openQuoteMail() {
    if (this.editMode) this.saveEdit(() => this.showQuoteMail());
    else this.showQuoteMail();
  }

  private showQuoteMail() {
    const current = this.translate.currentLang as MailLang;
    this.mailLang = this.mailLangs.includes(current) ? current : 'it';
    const expires = this.event?.quote_request?.expires_at;
    const remaining = this.quoteLink && expires
      ? Math.ceil((new Date(expires).getTime() - Date.now()) / 86400000)
      : 0;
    this.mailDays = remaining >= 1 ? Math.min(90, remaining) : this.profileQuoteDays;
    this.mailUntil = toDateInput(
      remaining >= 1 ? new Date(expires) : new Date(Date.now() + this.mailDays * 86400000),
    );
    this.mailLinkFresh = false;
    this.mailOpen = true;
    this.refreshQuoteMail();
  }

  closeQuoteMail() {
    this.mailOpen = false;
  }

  setMailIncludeLink(value: boolean) {
    this.mailIncludeLink = value;
    this.refreshQuoteMail();
  }

  get mailUntilMin(): string {
    return toDateInput(new Date());
  }

  get mailUntilMax(): string {
    return toDateInput(new Date(Date.now() + 365 * 86400000));
  }

  setMailValidityMode(mode: 'days' | 'date') {
    this.mailValidityMode = mode;
  }

  async setMailDays(value: number, input?: HTMLInputElement) {
    const days = Math.min(90, Math.max(1, Math.round(+value) || this.profileQuoteDays));
    if (!(await this.confirmNewLink())) {
      if (input) input.value = String(this.mailDays);
      return;
    }
    this.mailDays = days;
    this.mailUntil = toDateInput(new Date(Date.now() + days * 86400000));
    this.regenerateMailLink();
  }

  async setMailUntil(value: string, input?: HTMLInputElement) {
    if (!value || value < this.mailUntilMin || value > this.mailUntilMax || !(await this.confirmNewLink())) {
      if (input) input.value = this.mailUntil;
      return;
    }
    this.mailUntil = value;
    this.regenerateMailLink();
  }

  // Nuova validità = nuovo link: quello precedente smette di funzionare
  private async confirmNewLink(): Promise<boolean> {
    if (!this.mailIncludeLink || !this.quoteLink || this.mailLinkFresh) return true;
    const res = await this.swal.confirm(
      this.translate.instant('quote.mail.days_confirm_title'),
      this.translate.instant('quote.mail.days_confirm_text'),
      this.translate.instant('quote.mail.days_confirm'),
      this.translate.instant('common.cancel'),
    );
    return res.isConfirmed;
  }

  private get mailValidity(): { days?: number; expires_at?: string } {
    if (this.mailValidityMode === 'days') return { days: this.mailDays };
    const [y, m, d] = this.mailUntil.split('-').map(Number);
    return { expires_at: new Date(y, m - 1, d, 23, 59, 59).toISOString() };
  }

  private regenerateMailLink() {
    if (!this.mailIncludeLink) return;
    this.mailLinkFresh = true;
    this.createQuoteLink(() => this.renderQuoteMail(), this.mailValidity);
  }

  setMailLang(lang: MailLang) {
    this.mailLang = lang;
    this.renderQuoteMail();
  }

  private refreshQuoteMail() {
    if (this.mailIncludeLink && !this.quoteLink) {
      this.mailLinkFresh = true;
      this.createQuoteLink(() => this.renderQuoteMail(), this.mailValidity);
      return;
    }
    this.renderQuoteMail();
  }

  private renderQuoteMail() {
    const link = this.mailIncludeLink ? this.quoteLink : null;
    const mail = buildQuoteEmail({
      event: this.event,
      t: translatorFor(this.mailLang),
      locale: MAIL_LOCALES[this.mailLang],
      dateDisplay: this.event?.event?.date
        ? new Date(this.event.event.date).toLocaleDateString(MAIL_LOCALES[this.mailLang])
        : periodLabel(this.event?.event?.date_hint, translatorFor(this.mailLang)) ||
          translatorFor(this.mailLang)('crea.period.no_date'),
      isBeauty: this.isBeautyCategory,
      link,
      linkExpiry: this.event?.quote_request?.expires_at
        ? new Date(this.event.quote_request.expires_at).toLocaleDateString(MAIL_LOCALES[this.mailLang])
        : '',
      signature: this.profileSignature,
      logo: this.profileLogo,
    });
    this.mailRaw = mail.html;
    this.mailPlain = mail.text;
    this.mailHtml = this.sanitizer.bypassSecurityTrustHtml(mail.html);
  }

  async copyQuoteMail() {
    const ok = await copyRichText(this.mailRaw, this.mailPlain);
    if (ok) this.swal.success(this.translate.instant('quote.mail.copied'));
    else this.swal.error(this.translate.instant('quote.mail.copy_error'));
  }

  async shareQuote() {
    const url = this.quoteLink;
    if (!url) return;
    const outcome = await shareOrCopy({
      title: this.translate.instant('quote.share_title'),
      text: this.translate.instant('quote.share_text', {
        name: this.event?.client?.name ?? '',
        date: this.weddingDateDisplay,
      }),
      url,
    });
    if (outcome === 'copied') this.swal.success(this.translate.instant('riepilogo.hero.copied'));
  }

  get needsDate(): boolean {
    return needsDate(this.event);
  }

  setStatus(status: 'quote' | 'confirmed') {
    this.moreMenuOpen = false;
    if (status === 'confirmed' && !this.event?.event?.date) {
      this.swal.error(this.translate.instant('quote.confirm_date_needed'));
      return;
    }
    this.ms.updateStatus(
      this.eventId!,
      status,
      () => {
        this.event.event.status = status;
        if (status === 'confirmed') {
          this.event.quote_request = { ...(this.event.quote_request ?? {}), token: null, expires_at: null };
        }
        this.swal.success(
          this.translate.instant(status === 'confirmed' ? 'quote.confirmed_msg' : 'quote.reverted_msg'),
        );
      },
      () => this.swal.errorDialog(
        this.translate.instant('swal.error'),
        this.translate.instant('quote.status_error'),
      ),
    );
  }

  // ── INTESTAZIONE ──────────────────────────────────────────────────────────

  get countdown(): StatusChip | null {
    const days = daysFromToday(this.event?.event?.date);
    if (days === null) return null;
    if (days === 0) return { key: 'riepilogo.hero.today', tone: 'warn' };
    if (days === 1) return { key: 'riepilogo.hero.tomorrow', tone: 'warn' };
    if (days > 1) return { key: 'riepilogo.hero.in_days', tone: days <= 14 ? 'warn' : 'neutral', params: { n: days } };
    return { key: 'riepilogo.hero.past', tone: 'neutral' };
  }

  get contractStatus(): StatusChip {
    const ev = this.event;
    const sr = ev?.signature_request;
    if (!ev?.event?.contract?.file) return { key: 'riepilogo.hero.contract_todo', tone: 'warn' };
    if (ev.event.contract.is_signed || sr?.signed_contract_file || sr?.signed_at) {
      return { key: 'riepilogo.hero.contract_signed', tone: 'ok' };
    }
    const linkActive = sr?.token && (!sr.expires_at || new Date(sr.expires_at) > new Date());
    if (linkActive) return { key: 'riepilogo.hero.contract_waiting', tone: 'warn' };
    return { key: 'riepilogo.hero.contract_ready', tone: 'neutral' };
  }

  get paymentStatus(): StatusChip {
    if (this.totalAll <= 0) return { key: 'riepilogo.hero.pay_none', tone: 'neutral' };
    if (this.daIncassare <= 0) return { key: 'riepilogo.hero.pay_done', tone: 'ok' };
    const amount = this.formatEuro(this.daIncassare);
    if (this.store.balanceOverdue) return { key: 'riepilogo.hero.pay_overdue', tone: 'danger', params: { amount } };
    return { key: 'riepilogo.hero.pay_todo', tone: 'warn', params: { amount } };
  }

  get teamStatus(): StatusChip {
    const team = this.unifiedTeam;
    if (!team.length) return { key: 'riepilogo.hero.team_none', tone: 'neutral' };
    const pending = team.filter((m: any) => m._type === 'collaborator' && m.status !== 'accepted').length;
    if (pending) return { key: 'riepilogo.hero.team_pending', tone: 'warn', params: { n: pending } };
    return { key: 'riepilogo.hero.team_ok', tone: 'ok', params: { n: team.length } };
  }

  private formatEuro(v: number): string {
    return new Intl.NumberFormat(this.locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
  }

  get clientPhone(): string {
    return (this.event?.client?.phone ?? '').trim();
  }

  get clientEmail(): string {
    return (this.event?.client?.email ?? '').trim();
  }

  // wa.me vuole il numero internazionale senza "+": se manca il prefisso assumiamo l'Italia
  get whatsappUrl(): string | null {
    let digits = this.clientPhone.replace(/[^\d+]/g, '');
    if (!digits) return null;
    if (digits.startsWith('+')) digits = digits.slice(1);
    else if (digits.startsWith('00')) digits = digits.slice(2);
    else digits = '39' + digits;
    return `https://wa.me/${digits}`;
  }

  async shareEvent() {
    const ev = this.event?.event ?? {};
    const parts = [this.weddingDateDisplay, ev.time, ev.location].filter(Boolean);
    const outcome = await shareOrCopy({
      title: this.brideFullName,
      text: `${this.brideFullName}\n${parts.join(' · ')}`,
      url: window.location.href,
    });
    if (outcome === 'copied') this.swal.success(this.translate.instant('riepilogo.hero.copied'));
  }

  // ── CRONOLOGIA PROVA → EVENTO ─────────────────────────────────────────────

  get showTimeline(): boolean {
    return !this.editMode && !!this.event?.event?.test_date && this.canSee('trial') && this.canSee('event_data');
  }

  get trialPast(): boolean {
    return (daysFromToday(this.event?.event?.test_date) ?? 0) < 0;
  }

  get eventPast(): boolean {
    return (daysFromToday(this.event?.event?.date) ?? 0) < 0;
  }

  get nextStep(): 'trial' | 'event' | null {
    if (!this.trialPast) return 'trial';
    if (!this.eventPast) return 'event';
    return null;
  }

  // ── DATE ──────────────────────────────────────────────────────────────────

  get eventDateDisplay(): string {
    if (!this.event?.event?.date) return '';
    return new Date(this.event.event.date).toLocaleDateString(this.locale);
  }

  // Data precisa, altrimenti il periodo indicativo del preventivo
  get weddingDateDisplay(): string {
    return (
      this.eventDateDisplay ||
      periodLabel(this.event?.event?.date_hint, (k) => this.translate.instant(k)) ||
      this.translate.instant('crea.period.no_date')
    );
  }

  readonly periodOptions = PERIOD_OPTIONS;
  periodYears = periodYears();

  get showPeriodEdit(): boolean {
    return this.isQuote && !this.event?.event?.date;
  }

  get hintYear(): number | null {
    return this.event?.event?.date_hint?.year ?? null;
  }

  set hintYear(v: number | null) {
    this.event.event.date_hint = { ...(this.event.event.date_hint ?? {}), year: v };
  }

  get hintPeriod(): string {
    return this.event?.event?.date_hint?.period ?? '';
  }

  set hintPeriod(v: string) {
    this.event.event.date_hint = { ...(this.event.event.date_hint ?? {}), period: v };
  }

  get weddingDateInput(): string {
    if (!this.event?.event?.date) return '';
    return new Date(this.event.event.date).toISOString().split('T')[0];
  }

  set weddingDateInput(v: string) {
    this.event.event.date = v;
    if (v) this.event.event.date_hint = null;
  }

  get weddingDateTestDisplay(): string {
    if (!this.event?.event?.test_date) return '';
    return new Date(this.event.event.test_date).toLocaleDateString(
      this.locale,
    );
  }

  get weddingDateTestInput(): string {
    if (!this.event?.event?.test_date) return '';
    return new Date(this.event.event.test_date)
      .toISOString()
      .split('T')[0];
  }

  set weddingDateTestInput(v: string) {
    this.event.event.test_date = v;
  }

  // ── IMMAGINI ──────────────────────────────────────────────────────────────

  onSelect(e: any) {
    this.selectedFiles.push(...e.addedFiles);
  }

  onPhotoInput(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    this.selectedFiles = files;
    this.uploadImages();
  }

  onRemove(file: File) {
    this.selectedFiles = this.selectedFiles.filter((f) => f !== file);
  }

  getImageUrl(key: string): string {
    return `${environment.apiUrl}/events/${this.eventId}/image?key=${encodeURIComponent(key)}`;
  }

  uploadImages() {
    if (!this.selectedFiles.length) return;
    this.isUploadingImages = true;
    this.ms.uploadImmagini(
      this.eventId!,
      this.selectedFiles,
      (data: any) => {
        if (data?.image) this.event.image = data.image;
        this.selectedFiles = [];
        this.isUploadingImages = false;
        this.swal.success('Immagini caricate!');
      },
      () => {
        this.isUploadingImages = false;
        this.swal.errorDialog('Errore', 'Caricamento immagini fallito');
      },
    );
  }

  saveImageMeta(img: any) {
    this.ms.updateImmagine(
      this.eventId!,
      img.key,
      { name: (img.name ?? '').trim(), note: (img.note ?? '').trim() },
      (data: any) => {
        if (data?.image) this.event.image = data.image;
        this.swal.success('Foto aggiornata');
      },
      () => this.swal.errorDialog('Errore', 'Aggiornamento foto fallito'),
    );
  }

  deleteImage(img: any) {
    this.swal
      .confirm('Elimina foto', 'Eliminare questa foto?')
      .then((result) => {
        if (!result.isConfirmed) return;
        this.ms.deleteImmagine(
          this.eventId!,
          img.key,
          (data: any) => {
            if (data?.image) this.event.image = data.image;
            if (this.lightboxIndex >= (this.event?.image?.gallery?.length ?? 0)) {
              this.lightboxIndex = Math.max(0, (this.event?.image?.gallery?.length ?? 0) - 1);
            }
            if (!this.event?.image?.gallery?.length) this.closeLightbox();
            this.swal.success('Foto eliminata');
          },
          () => this.swal.errorDialog('Errore', 'Eliminazione foto fallita'),
        );
      });
  }

  // ── LOGISTICA ─────────────────────────────────────────────────────────────

  get saldoRimanente(): number {
    return this.ms.calcolaSaldo(this.event);
  }

  // ── TEAM ──────────────────────────────────────────────────────────────────

  showTeamPicker = false;

  openTeamPicker() {
    if (!this.editMode) this.startEdit();
    this.showTeamPicker = true;
  }

  // Confronto robusto: per email se presente, altrimenti per nome (contatti senza email)
  private samePerson(p: any, member: any): boolean {
    if (member.email) {
      return (p.email ?? '').toLowerCase() === member.email.toLowerCase();
    }
    return !p.email && p.name === member.name;
  }

  isTeamMemberAssigned(member: any): boolean {
    return this.event.schedule.team.people.some((p: any) =>
      this.samePerson(p, member),
    );
  }

  toggleTeamMember(member: any) {
    const people = this.event.schedule.team.people;
    const index = people.findIndex((p: any) => this.samePerson(p, member));
    if (index >= 0) {
      people.splice(index, 1);
    } else {
      people.push({
        name: member.name,
        lastname: member.lastname,
        email: member.email,
        role: member.service,
        notes: '',
      });
      // Membro del Team con accesso Nyzed: vede già il matrimonio nel suo login
      // (filtro su schedule.team.people). Nessun invito collaborazione da accettare.
      if (member._hasAccess) {
        this.swal.success(
          `${member.name} vedrà questo matrimonio nel suo accesso al team.`,
        );
      } else if (member.email) {
        this.swal
          .confirm(
            'Invia invito collaborazione',
            `Vuoi inviare un invito a ${member.name} ${member.lastname ?? ''} (${member.email})?`,
            'Invia',
            'Non ora',
          )
          .then((result) => {
            if (result.isConfirmed) {
              this.ms.inviteCollaborator(this.eventId!, member.email);
            }
          });
      }
    }
  }

  getAssignedMemberNotes(member: any): string {
    const p = this.event.schedule.team.people.find((p: any) =>
      this.samePerson(p, member),
    );
    return p?.notes ?? '';
  }

  setAssignedMemberNotes(member: any, notes: string) {
    const p = this.event.schedule.team.people.find((p: any) =>
      this.samePerson(p, member),
    );
    if (p) p.notes = notes;
  }

  getRoleLabel(service: any): string {
    if (!service) return '';
    const parts = [];
    if (service.hair) parts.push('Capelli');
    if (service.makeup) parts.push('Trucco');
    if (service.photo) parts.push('Foto');
    return parts.join(', ') || '—';
  }

  getInitials(name: string, lastname?: string): string {
    const n = name?.charAt(0)?.toUpperCase() ?? '';
    const l = lastname?.charAt(0)?.toUpperCase() ?? '';
    return n + l;
  }

  getAvatarColor(name: string): string {
    const colors = [
      '#c9a96e', '#c4857a', '#a8b5a2', '#9b8ea8',
      '#b5987a', '#7a9eb5', '#b5a87a', '#8ab5a0',
    ];
    let hash = 0;
    for (let i = 0; i < (name?.length ?? 0); i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  // Gruppo TEAM: membri con accesso Nyzed (arricchiti con i ruoli della rubrica se l'email coincide)
  get teamGroup(): any[] {
    const rubricaByEmail = new Map<string, any>(
      this.profileTeam
        .filter((m: any) => m.email)
        .map((m: any) => [(m.email as string).toLowerCase(), m]),
    );
    return this.teamAccessMembers.map((m: any) => {
      const r = rubricaByEmail.get((m.email ?? '').toLowerCase());
      return r ? { ...r, _hasAccess: true } : m;
    });
  }

  // Gruppo COLLABORATORI: contatti della rubrica senza accesso Nyzed
  get collaboratoriGroup(): any[] {
    const accessEmails = new Set(
      this.teamAccessMembers
        .map((m: any) => (m.email ?? '').toLowerCase())
        .filter(Boolean),
    );
    return this.profileTeam.filter(
      (m: any) =>
        m.available !== false &&
        !accessEmails.has((m.email ?? '').toLowerCase()),
    );
  }

  // ── PARTECIPANTI ──────────────────────────────────────────────────────────

  ruoliPartecipanti = [
    'Damigella', 'Mamma sposa', 'Mamma sposo', 'Amica/o', 'Altro',
  ];

  newPartecipante = this.emptyPartecipante();

  emptyPartecipante() {
    return { name: '', role: 'Damigella', hair: false, makeup: false, notes: '' };
  }

  addPartecipante() {
    if (!this.event.schedule.partecipanti) {
      this.event.schedule.partecipanti = [];
    }
    this.event.schedule.partecipanti.push({ ...this.newPartecipante });
    this.newPartecipante = this.emptyPartecipante();
  }

  removePartecipante(i: number) {
    this.event.schedule.partecipanti.splice(i, 1);
  }

  // ── SERVIZI ───────────────────────────────────────────────────────────────

  isServiceSelected(service: any): boolean {
    return this.event.event.services.some(
      (s: any) => s.name === service.name && s.selected,
    );
  }

  toggleService(service: any) {
    const existing = this.event.event.services.find(
      (s: any) => s.name === service.name,
    );
    if (existing) {
      existing.selected = !existing.selected;
    } else {
      this.event.event.services.push({
        name: service.name,
        price: effectivePrice(service),
        ...discountFields(service),
        description: service.description,
        selected: true,
      });
    }
  }

  getServicePrice(service: any): number {
    const existing = this.event.event.services.find(
      (s: any) => s.name === service.name,
    );
    return existing?.price ?? effectivePrice(service);
  }

  setServicePrice(service: any, price: number) {
    const existing = this.event.event.services.find(
      (s: any) => s.name === service.name,
    );
    if (existing) existing.price = price;
  }

  get eventCollaborators(): any[] {
    return this.event?.collaborators ?? [];
  }

  get weddingCollaborators(): any[] {
    return this.eventCollaborators;
  }

  get pendingCollaborators(): any[] {
    return this.eventCollaborators.filter((c: any) => c.status === 'pending');
  }

  get acceptedCollaborators(): any[] {
    return this.eventCollaborators.filter((c: any) => c.status === 'accepted');
  }

  deleteEvent() {
    this.swal
      .confirm(
        'Elimina evento',
        'Questa azione è irreversibile: verranno cancellati anche foto, documenti e contratto collegati. Continuare?',
        'Elimina',
      )
      .then((result) => {
        if (!result.isConfirmed) return;
        this.ms.deleteEvent(
          this.eventId!,
          () => {
            this.swal.success('Evento eliminato');
            this.router.navigate(['/eventi']);
          },
          () => this.swal.errorDialog('Errore', 'Eliminazione evento fallita'),
        );
      });
  }

  removeFromTeam(member: any) {
    this.swal
      .confirm('Rimuovi dal team', `Sei sicura di voler rimuovere ${member.name}?`)
      .then((result) => {
        if (!result.isConfirmed) return;

        if (member._type === 'collaborator') {
          this.ms.removeCollaborator(this.eventId!, member.user_id, () => {
            this.event.collaborators = this.event.collaborators.filter(
              (c: any) => c.user_id !== member.user_id,
            );
            this.swal.success('Collaboratore rimosso');
          });
        } else {
          const people = this.event.schedule.team.people;
          const idx = people.findIndex(
            (p: any) => p.name === member.name && p.email === member.email,
          );
          if (idx >= 0) people.splice(idx, 1);
          // In lettura una proposta solo guardata non va salvata
          const viewed = activeVariantId(this.event);
          const restoreViewed = !this.editMode && !!this.savedVariantId && viewed !== this.savedVariantId;
          if (restoreViewed) switchVariant(this.event, this.savedVariantId!);
          this.ms.saveMatrimonio(this.eventId!, this.event, () => {
            if (this.editMode) this.store.takeSnapshot();
            if (restoreViewed && viewed) switchVariant(this.event, viewed);
          });
        }
      });
  }

  get rejectedCollaborators(): any[] {
    return this.eventCollaborators.filter((c: any) => c.status === 'rejected');
  }

  get teamOnlyMembers(): any[] {
    const invitedEmails = this.eventCollaborators.map((c: any) => c.email);
    return (this.event?.schedule?.team?.people ?? []).filter(
      (p: any) => !p.email || !invitedEmails.includes(p.email),
    );
  }

  get unifiedTeam(): any[] {
    const result: any[] = [];
    for (const p of this.teamOnlyMembers) {
      result.push({ ...p, _type: 'assigned' });
    }
    for (const c of this.eventCollaborators) {
      result.push({ ...c, _type: 'collaborator' });
    }
    return result;
  }

  removeCollaborator(userId: string) {
    this.swal
      .confirm('Rimuovi collaboratore', 'Sei sicuro di voler rimuovere questo collaboratore?')
      .then((result) => {
        if (result.isConfirmed) {
          this.ms.removeCollaborator(this.eventId!, userId, () => {
            this.event.collaborators = this.event.collaborators.filter(
              (c: any) => c.user_id !== userId,
            );
            this.swal.success('Collaboratore rimosso');
          });
        }
      });
  }

  resendInvite(userId: string) {
    this.ms.resendInvite(this.eventId!, userId);
  }

  get customServices(): any[] {
    return (this.event?.event?.services ?? []).filter((s: any) => s.custom);
  }

  addCustomService() {
    this.event.event.services.unshift({
      name: '',
      price: null,
      description: '',
      custom: true,
      selected: true,
      quantity: 1,
    });
  }

  removeCustomService(service: any) {
    this.event.event.services = this.event.event.services.filter((s: any) => s !== service);
  }

  max1(n: number): number {
    return Math.max(1, n);
  }

  getServiceQuantity(service: any): number {
    const existing = this.event.event.services.find(
      (s: any) => s.name === service.name,
    );
    return existing?.quantity ?? 1;
  }

  setServiceQuantity(service: any, quantity: number) {
    const existing = this.event.event.services.find(
      (s: any) => s.name === service.name,
    );
    if (existing) existing.quantity = Math.max(1, quantity);
  }

  ngOnDestroy() {
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
    this.stopReturnWatch?.();
  }

  // Alias per compatibilità con il template
  get matrimonio(): any { return this.event; }
  get matrimonioId(): string | null { return this.eventId; }
  get brideFullName(): string { return this.clientFullName; }
}

function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
